import React, { useState } from "react";
import { Modal } from "@material-ui/core";
import Select from "react-select";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";

import { createGroupChat } from "../../redux/chats/actions";
import SoftInput from "../SoftInput";
import SoftBox from "../SoftBox";
import SoftButton from "../SoftButton";
import SoftTypography from "../SoftTypography";
import SoftSelect from "../SoftSelect";
import { useTheme, Divider, Dialog } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const GroupChatModal = ({ openModal, setOpenModal, users }) => {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const theme = useTheme();
  const {
    primary: { main: primaryColor },
  } = theme.palette;

  const closeDialog = () => setOpenModal(false);

  const onChangeTrigger = async (e) => {
    setSearchKeyword(e.target.value);
  };

  const createGroupChatTrigger = async () => {
    setLoading(true);

    try {
      if (searchKeyword === "" || !selectedUsers.length) {
        toast.error("Please fill both fields");
      } else if (selectedUsers.length < 2) {
        toast.error("More than 2 users are required to form a group chat");
      } else {
        const userIds = selectedUsers.map((item) => item.value);
        const payload = {
          name: searchKeyword,
          users: userIds,
        };

        dispatch(createGroupChat(payload));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
    }
    setLoading(false);
    setOpenModal(false);
  };

  return (
    <Dialog
      open={openModal}
      onClose={closeDialog}
      sx={{
        ".MuiPaper-root": {
          padding: { mini: "1rem", md: "1.5rem 1.7rem" },
          borderRadius: "24px",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.60) 100%)",
          boxShadow:
            "0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
          backdropFilter: "blur(18px) saturate(160%)",
          width: "100%",
          maxWidth: "600px",
          border: "1px solid rgba(255,255,255,0.6)",
          // Lets the "Select group member" dropdown list float past the
          // dialog's own bottom edge instead of being clipped by it — same
          // reason the Player's Scorecard player-search dropdown (Timeline.jsx)
          // isn't clipped by its own card.
          overflow: "visible",
          "::-webkit-scrollbar": {
            display: "none",
            height: 0,
            width: 0,
          },
        },
      }}
    >
      <SoftBox display="flex" justifyContent="space-between">
        <SoftBox>
          <SoftTypography variant="h4" sx={{ fontSize: "20px", color: "#0F172A" }}>
            Create Group Chat
          </SoftTypography>
        </SoftBox>

        <SoftButton
          variant="outlined"
          color="dark"
          onClick={closeDialog}
          sx={{
            "& svg": { fontSize: "1.25rem !important" },
            borderRadius: "50%",
            minWidth: "max-content",
            minHeight: "max-content",
            width: "max-content",
            height: "max-content",
            padding: "0.25rem",
          }}
        >
          <CloseRoundedIcon />
        </SoftButton>
      </SoftBox>

      <Divider
        sx={{
          backgroundColor: "rgba(15,23,42,0.10)",
        }}
      />

      <SoftBox>
        <SoftInput
          placeholder="Group name"
          value={searchKeyword}
          onChange={onChangeTrigger}
          required
          sx={{
            marginBottom: 3,
            backgroundColor: "#F6F7F9 !important",
            borderColor: "#E4E7EC",
            color: "#1d2939 !important",
            "& .MuiInputBase-input::placeholder": {
              color: "#98A2B3 !important",
              opacity: "0.7",
            },
            "&.Mui-focused": {
              borderColor: primaryColor,
              boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}`,
            },
          }}
        />
        <SoftSelect
          isLoading={false}
          options={users}
          isMulti
          name="kpimodels"
          value={selectedUsers}
          onChange={(selectedOption) => {
            setSelectedUsers(selectedOption);
          }}
          placeholder="Select group member"
          darkTheme={false}
          styles={{
            control: {
              backgroundColor: "#F6F7F9 !important",
              height: "100%",
              minWidth: "2.5rem",
            },
            placeholder: {
              color: "#98A2B3 !important",
              opacity: "0.7",
            },
            input: {
              color: "#1d2939 !important",
              opacity: "0.7",
            },
            singleValue: {
              color: "#1d2939 !important",
              opacity: "0.7",
            },
            valueContainer: {
              flexFlow: "row wrap",
              gap: "0.25rem",
              padding: "0.5rem 0.75rem",
            },
            indicatorsContainer: {
              alignItems: "start",
            },
            // Light theme for the open dropdown list — SoftSelect defaults
            // to a dark (near-black bg / white text) menu that was never
            // overridden here, the only piece of this modal left dark.
            // Padding/shape copied from the Player's Scorecard
            // player-search dropdown (Timeline.jsx) for consistency.
            menu: {
              zIndex: 40,
              marginTop: "8px",
              paddingTop: "8px",
              paddingBottom: "8px",
              paddingLeft: "4px",
              paddingRight: "8px",
              backgroundColor: "#FFFFFF",
              border: "1px solid #EAECF0",
              borderRadius: "12px",
              boxShadow: "0px 8px 24px rgba(16, 24, 40, 0.12)",
            },
            menuList: {
              maxHeight: "260px",
              padding: 0,
              scrollbarWidth: "thin",
              scrollbarColor: "#D0D5DD transparent",
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": { backgroundColor: "#D0D5DD", borderRadius: "3px" },
            },
            option: {
              minWidth: 0,
              padding: "10px 12px 10px 8px",
              color: "#344054",
              fontSize: "14px",
              fontWeight: 500,
              backgroundColor: "#FFFFFF",
              borderRadius: "8px",
              "&[aria-selected='true']": { color: primaryColor, backgroundColor: `${primaryColor}14` },
              "&:hover, &:focus": { color: "#101828", backgroundColor: "#F2F4F7" },
            },
            // Selected-member chips were dark (gradient bg / white text) by
            // default too — re-themed light to match the rest of the modal.
            multiValue: {
              backgroundColor: `${primaryColor}14`,
              "& div:first-of-type": {
                color: primaryColor,
              },
              "& div:last-of-type": {
                color: primaryColor,
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "#101828",
                },
              },
            },
          }}
        />
      </SoftBox>

      <Divider
        sx={{
          backgroundColor: "rgba(15,23,42,0.10)",
        }}
      />

      <SoftBox
        sx={{
          display: "flex",
          justifyContent: "end",
        }}
      >
        <SoftButton
          onClick={createGroupChatTrigger}
          color="primary"
          sx={{
            color: "white.main",
            textTransform: "none",
            borderRadius: "4px",
            width: "max-content",
          }}
          size="small"
          loading={loading}
          disabled={loading}
          variant="gradient"
        >
          Create
        </SoftButton>
      </SoftBox>
    </Dialog>
  );
};

export default GroupChatModal;
